/**
 * pageHierarchy — editor-side mutations for the sitemap's page hierarchy.
 *
 * The DETERMINISTIC parent-selection rule (and the read walks) are shared with
 * the published-nav renderer and live in `@website-builder/schemas` (`nav.ts`)
 * so the editor and the rendered menu can never disagree. This module adds the
 * editor-only mutations (reparent/detach) and pickers on top of that rule.
 *
 * Edges are nav primitives AND (v1) hierarchy: a node's PARENT is the source of
 * its incoming edge. "B is a child of A" = edge {source:A, target:B}.
 */
import {
  type NavEdge,
  navParentOf,
  navChildrenOf,
  navDescendantIds,
} from "@website-builder/schemas";

/** Minimal edge shape (xyflow Edge is a superset). */
export type HEdge = NavEdge;

// Cross-platform edge id. The browser/Node have a global `crypto.randomUUID`;
// React Native does NOT (no global `crypto`) — `crypto.randomUUID()` throws
// "Property 'crypto' doesn't exist". Edge ids aren't security-sensitive, so
// fall back to a timestamp+counter id on native (mirrors pageTemplates' pageId).
let _edgeCounter = 0;
function edgeId(): string {
  const c = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto;
  if (typeof c?.randomUUID === "function") return c.randomUUID();
  return `edge-${Date.now().toString(36)}-${++_edgeCounter}`;
}

// Re-export the shared read helpers under editor-local names.
export const parentOf = navParentOf;
export const childrenOf = navChildrenOf;
export const descendantIds = navDescendantIds;

/** All transitive ancestor ids of `nodeId` (cycle-safe; excludes node). */
export function ancestorIds(
  edges: readonly HEdge[],
  nodeId: string,
): Set<string> {
  const out = new Set<string>();
  let cur = navParentOf(edges, nodeId);
  while (cur && !out.has(cur) && cur !== nodeId) {
    out.add(cur);
    cur = navParentOf(edges, cur);
  }
  return out;
}

/**
 * Make `childId` a single-parent child of `parentId`: drop every existing
 * incoming edge to `childId`, then add `parentId → childId`. No-op (changed:
 * false) for a self-parent, a cycle (parent is a descendant of child), or when
 * the exact parent→child edge already is the sole incoming edge.
 *
 * `newEdge()` defaults to a cross-platform edge id; injectable for tests.
 */
export function reparent<E extends HEdge>(
  edges: readonly E[],
  parentId: string,
  childId: string,
  newEdge: (source: string, target: string) => E = (source, target) =>
    ({ id: edgeId(), source, target }) as E,
): { edges: E[]; changed: boolean } {
  if (parentId === childId) return { edges: [...edges], changed: false };
  if (navDescendantIds(edges, childId).has(parentId)) {
    return { edges: [...edges], changed: false }; // would create a cycle
  }
  const incoming = edges.filter((e) => e.target === childId);
  const alreadySole = incoming.length === 1 && incoming[0]!.source === parentId;
  if (alreadySole) return { edges: [...edges], changed: false };

  const kept = edges.filter((e) => e.target !== childId);
  kept.push(newEdge(parentId, childId));
  return { edges: kept, changed: true };
}

/** Remove `childId`'s parent link(s) — it becomes a root. */
export function detach<E extends HEdge>(
  edges: readonly E[],
  childId: string,
): { edges: E[]; changed: boolean } {
  const kept = edges.filter((e) => e.target !== childId);
  return { edges: kept, changed: kept.length !== edges.length };
}

/**
 * Nodes a given `childId` may be moved under: every node except itself and its
 * own descendants (which would create a cycle). Preserves input node order.
 */
export function candidateParents<N extends { id: string }>(
  nodes: readonly N[],
  edges: readonly HEdge[],
  childId: string,
): N[] {
  const banned = navDescendantIds(edges, childId);
  banned.add(childId);
  return nodes.filter((n) => !banned.has(n.id));
}

/** A candidate target's bounding box (top-left origin), in flow coords. */
export interface DropCandidate {
  readonly id: string;
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
}

/**
 * The page the dragged node would nest under: the candidate whose box contains
 * the dragged node's `center`, excluding the dragged node itself and its
 * descendants (which would cycle). When several boxes contain the center, the
 * smallest-area (innermost) wins. Returns null if none qualifies — the basis
 * for the "explicit intent" drag-to-parent gesture (center must land on a
 * valid target, not mere overlap).
 */
export function pickDropParent(
  draggedId: string,
  center: { x: number; y: number },
  candidates: readonly DropCandidate[],
  edges: readonly HEdge[],
): string | null {
  const banned = navDescendantIds(edges, draggedId);
  banned.add(draggedId);
  let best: DropCandidate | null = null;
  for (const c of candidates) {
    if (banned.has(c.id)) continue;
    const inside =
      center.x >= c.x &&
      center.x <= c.x + c.w &&
      center.y >= c.y &&
      center.y <= c.y + c.h;
    if (!inside) continue;
    if (!best || c.w * c.h < best.w * best.h) best = c;
  }
  return best?.id ?? null;
}
